import { defineResource } from '@tnn/core'
import { z } from 'zod'
import { businessTypes } from '../party'
import { signingField } from '../signature'
import { type ServiceLine, serviceLineSchema, servicesFeeOf } from './service'

/**
 * A waybill (บิลงานขนส่ง) is what a customer sends through the branch network:
 * sender, receiver and the goods. Trucks move it along `route` (branch ids) and
 * the last branch delivers it or hands it over at the counter.
 */
export const waybillStatuses = [
  'draft',
  'received',
  'in_transit',
  'at_branch',
  'out_for_delivery',
  'ready_for_pickup',
  'delivered',
  'returned',
  'cancelled',
] as const
export type WaybillStatus = (typeof waybillStatuses)[number]
/** Where a bill is still on its way: not yet delivered, returned or cancelled */
export const waybillUnderway: readonly WaybillStatus[] = ['received', 'in_transit', 'at_branch', 'out_for_delivery', 'ready_for_pickup']

/** The two parties of a bill: the sender at the origin, the receiver at the destination */
export const paySides = ['sender', 'receiver'] as const
export type PaySide = (typeof paySides)[number]
/** Who pays the freight, all of it: the sender at the counter, or the receiver at the destination (freight collect) */
export const waybillPayers = paySides
/** `dropoff`: the sender brings it to the counter; `pickup`: we collect it */
export const waybillOriginModes = ['dropoff', 'pickup'] as const
/** `door`: delivered to the receiver's address; `branch`: the receiver collects it at a branch */
export const waybillDestModes = ['door', 'branch'] as const
/**
 * `partial`: part of the freight was paid at the counter, the rest is still owed there.
 * `overpaid`: a party paid beyond their part (the price was lowered after the money was taken): a refund is owed.
 * `moved`: sent back, what the receiver still owed went onto the return bill (`receiverMoved`) and nothing else is owed
 */
export const waybillPaymentStatuses = ['unpaid', 'partial', 'paid', 'overpaid', 'moved'] as const
export type WaybillPaymentStatus = (typeof waybillPaymentStatuses)[number]

export const waybillItemSchema = z.object({
  productId: z.string().nullish(),
  name: z.string().trim().min(1).max(200),
  qty: z.number().int().min(1),
  unit: z.string().trim().max(30).nullish(),
  /** Per piece */
  weightKg: z.number().min(0).nullish(),
  /** Freight per piece */
  rate: z.number().min(0),
  /** A charge, not goods (a return bill's freight lines): counts in the freight, not in the pieces or weight */
  charge: z.boolean().optional(),
})
export type WaybillItem = z.infer<typeof waybillItemSchema>

const text = (max: number) => z.string().trim().max(max).nullish()

export const waybill = defineResource({
  app: 'tms',
  name: 'waybill',
  path: 'waybills',
  schema: z.object({
    bookedAt: z.iso.datetime({ offset: true }),
    payer: z.enum(waybillPayers).default('sender'),
    originBranchId: z.string().min(1),
    originMode: z.enum(waybillOriginModes).default('dropoff'),
    senderId: z.string().nullish(),
    senderName: z.string().trim().min(1).max(200),
    senderPhone: text(100),
    senderIdCard: text(30),
    senderAddress: z.string().max(500).nullish(),
    senderSubdistrict: text(100),
    senderDistrict: text(100),
    senderProvince: text(100),
    senderPostcode: text(10),
    /** Copied from the contact like the rest, so a bill keeps the trade it was booked under */
    senderBusinessType: z.enum(businessTypes).nullish(),
    destMode: z.enum(waybillDestModes).default('door'),
    /** Empty: the branch that covers the receiver's district or province */
    destBranchId: z.string().nullish(),
    receiverId: z.string().nullish(),
    receiverName: z.string().trim().min(1).max(200),
    receiverPhone: text(100),
    receiverAddress: z.string().max(500).nullish(),
    receiverSubdistrict: text(100),
    receiverDistrict: text(100),
    receiverProvince: text(100),
    receiverPostcode: text(10),
    receiverBusinessType: z.enum(businessTypes).nullish(),
    items: z.array(waybillItemSchema).min(1),
    /** Add-on services (`services`): carrying upstairs… each priced on the pieces or kg it says (`serviceAmount`) */
    services: z.array(serviceLineSchema).default([]),
    declaredValue: z.number().min(0).nullish(),
    reference: text(100),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 124, priority: 1 } },
    bookedAt: { type: 'datetime', filter: true, list: { width: 160, priority: 2 }, form: { span: 4, section: 'booking' } },
    payer: { filter: true, list: { width: 110, priority: 3, hidden: true }, form: { span: 4, section: 'booking' } },
    originBranchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { priority: 3, hidden: true },
      form: { span: 4, section: 'booking' },
    },
    originMode: { list: false, form: { span: 12, section: 'sender' } },
    senderId: { type: 'ref', ref: { resource: 'tms.contact' }, list: false, form: { hidden: true }, import: false },
    senderName: { search: true, list: { priority: 1 }, form: { span: 12, section: 'sender', custom: true } },
    senderPhone: { search: true, list: false, form: { span: 6, section: 'sender' } },
    senderIdCard: { list: false, form: { span: 6, section: 'sender' } },
    // Copied from the contact: out of the counter's way, under more details
    senderBusinessType: {
      optionLabels: 'tms:businessType',
      filter: true,
      list: { width: 180, priority: 3, hidden: true },
      form: { span: 6, section: 'extra' },
    },
    senderAddress: { type: 'textarea', list: false, form: { span: 12, section: 'sender' } },
    senderProvince: { list: false, form: { span: 12, section: 'sender', custom: true } },
    senderDistrict: { list: false, form: { hidden: true } },
    senderSubdistrict: { list: false, form: { hidden: true } },
    senderPostcode: { list: false, form: { hidden: true } },
    destMode: { list: false, form: { span: 6, section: 'receiver' } },
    destBranchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { width: 150, priority: 2 },
      form: { span: 6, section: 'receiver', custom: true },
    },
    receiverId: { type: 'ref', ref: { resource: 'tms.contact' }, list: false, form: { hidden: true }, import: false },
    receiverName: { search: true, list: { priority: 1 }, form: { span: 12, section: 'receiver', custom: true } },
    receiverPhone: { search: true, list: false, form: { span: 12, section: 'receiver' } },
    receiverBusinessType: {
      optionLabels: 'tms:businessType',
      filter: true,
      list: { width: 180, priority: 3, hidden: true },
      form: { span: 6, section: 'extra' },
    },
    receiverAddress: { type: 'textarea', list: false, form: { span: 12, section: 'receiver' } },
    receiverProvince: { filter: true, list: { width: 150, priority: 2 }, form: { span: 12, section: 'receiver', custom: true } },
    receiverDistrict: { list: false, form: { hidden: true } },
    receiverSubdistrict: { list: false, form: { hidden: true } },
    receiverPostcode: { list: false, form: { hidden: true } },
    items: { type: 'json', list: false, form: { span: 12, section: 'items', custom: true }, import: false },
    pieces: { readOnly: true, type: 'integer', list: { width: 84, align: 'end', priority: 1 } },
    weightKg: { readOnly: true, type: 'number', list: { width: 110, align: 'end', priority: 3, hidden: true } },
    services: { type: 'json', list: false, form: { span: 12, section: 'services', custom: true }, import: false },
    /** The services' part of `fee` */
    servicesFee: { readOnly: true, type: 'money', list: { width: 120, align: 'end', priority: 3, hidden: true } },
    /** What the bill costs in all: the freight of its lines and its services (`waybillTotals`) */
    fee: { readOnly: true, type: 'money', list: { width: 110, align: 'end', priority: 1 } },
    /** The shipping terms its sender agreed to: those in effect when it was submitted (terms.ts) */
    // The shipping terms' version it keeps, by its number (terms.ts)
    termsId: { readOnly: true, list: false },
    route: { readOnly: true, type: 'json', list: false, export: false },
    currentBranchId: {
      readOnly: true,
      type: 'ref',
      ref: { resource: 'company.branch' },
      filter: true,
      list: { priority: 3, hidden: true },
    },
    status: {
      readOnly: true,
      type: 'select',
      options: waybillStatuses,
      filter: true,
      tones: {
        draft: 'neutral',
        received: 'info',
        in_transit: 'progress',
        at_branch: 'info',
        out_for_delivery: 'progress',
        ready_for_pickup: 'warning',
        delivered: 'success',
        returned: 'warning',
        cancelled: 'danger',
      },
      list: { width: 150, priority: 1 },
    },
    paymentStatus: {
      readOnly: true,
      type: 'select',
      options: waybillPaymentStatuses,
      filter: true,
      tones: { unpaid: 'warning', partial: 'progress', paid: 'success', overpaid: 'danger', moved: 'neutral' },
      list: { width: 120, priority: 2 },
    },
    /** Taken so far from each party: by the receipts below (see `waybillOwed`) */
    senderPaid: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    receiverPaid: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** Issued receipts that took (part of) this bill's freight: one, or more when it was paid in parts */
    receiptIds: { readOnly: true, type: 'multiselect', ref: { resource: 'tms.receipt' }, list: false },
    /** The customer's tracking page (`/track/<code>`), given on submit: random, so a printed QR never breaks (server-managed) */
    trackingCode: { readOnly: true, list: false, export: false },
    /** Sent back (ตีกลับ): the return bill this one became, and on the return bill the bill it brings back */
    returnId: { readOnly: true, type: 'ref', ref: { resource: 'tms.waybill' }, list: false },
    returnOfId: { readOnly: true, type: 'ref', ref: { resource: 'tms.waybill' }, list: false },
    /** What the receiver still owed when it was sent back, moved onto the return bill: they owe it no more (`waybillOwed`) */
    receiverMoved: { readOnly: true, type: 'money', list: false },
    declaredValue: { type: 'money', list: false, form: { span: 6, section: 'extra' } },
    reference: { search: true, list: { priority: 3, hidden: true }, form: { span: 6, section: 'extra' } },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'extra' } },
    /** The sender's acceptance of the terms: signed, a link waiting, or not yet (server-set) */
    signing: signingField,
  },
  title: 'number',
  columns: [
    'number',
    'bookedAt',
    'senderName',
    'receiverName',
    'receiverProvince',
    'destBranchId',
    'pieces',
    'fee',
    'paymentStatus',
    'status',
    'signing',
    'payer',
    'originBranchId',
    'currentBranchId',
    'weightKg',
    'servicesFee',
    'reference',
    'senderBusinessType',
    'receiverBusinessType',
  ],
  sections: ['booking', 'sender', 'receiver', 'items', 'services', 'extra'],
  defaultSort: '-bookedAt',
  // `handover`: the receiver takes it at its destination branch with nothing owed there (paying there hands it over too);
  // `undeliver`: undo a handover recorded by mistake (`@branch`: where it was handed over is one's home branch; whoever
  // recorded it may on the same day without it); `return`: send it back to the sender on a new bill (`@branch`: where it
  // stands is one's home branch)
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'handover', 'undeliver', 'return'],
  // `@branch` permissions cover the bills a branch sends or receives
  branchFields: ['originBranchId', 'destBranchId'],
  // Submitting numbers the bill and puts it on the network; paying needs a submitted bill
  // Every field stays editable after submit ("edit after submit", logged); paying needs a submitted bill
  submittable: true,

  card: { title: 'receiverName', subtitle: 'number', meta: ['receiverProvince', 'pieces', 'fee'], status: 'status' },
})

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

/**
 * Piece count, total weight, freight of a waybill's lines (a charge line is freight only) and what its services add
 * (each on what it says it is for, not these goods); `fee` is the two together, what the bill costs
 */
export function waybillTotals(
  items: readonly Pick<WaybillItem, 'qty' | 'weightKg' | 'rate' | 'charge'>[],
  services?: readonly ServiceLine[] | null,
) {
  const goods = items.filter((i) => !i.charge)
  const pieces = goods.reduce((sum, i) => sum + (i.qty || 0), 0)
  const weightKg = round2(goods.reduce((sum, i) => sum + (i.qty || 0) * (i.weightKg || 0), 0))
  const freight = round2(items.reduce((sum, i) => sum + (i.qty || 0) * (i.rate || 0), 0))
  const servicesFee = servicesFeeOf(services)
  return { pieces, weightKg, freight, servicesFee, fee: round2(freight + servicesFee) }
}

/** `waybillTotals` of a bill as a form or a row holds it: numbers typed as text read as numbers */
export function waybillTotalsOf(bill: Readonly<Record<string, unknown>>) {
  const items = ((bill.items ?? []) as WaybillItem[]).map((i) => ({
    qty: Number(i.qty) || 0,
    weightKg: Number(i.weightKg) || 0,
    rate: Number(i.rate) || 0,
    charge: i.charge,
  }))
  return waybillTotals(items, bill.services as ServiceLine[] | null)
}

type Dues = Record<PaySide, number>
/** A waybill as the payment helpers read it (a record or a row): payer, fee, senderPaid / receiverPaid */
export type WaybillValues = Readonly<Record<string, unknown>>

/** What each party owes on a bill: the whole freight to the payer, nothing to the other */
export function waybillDues(bill: WaybillValues): Dues {
  const fee = round2(Number(bill.fee) || 0)
  return bill.payer === 'receiver' ? { sender: 0, receiver: fee } : { sender: fee, receiver: 0 }
}

/** Still to take from each party: what they owe less what their receipts took (and the receiver's part moved onto a return bill) */
export function waybillOwed(bill: WaybillValues): Dues {
  const dues = waybillDues(bill)
  const moved = { sender: 0, receiver: Number(bill.receiverMoved) || 0 }
  const left = (side: PaySide) => round2(Math.max(dues[side] - (Number(bill[`${side}Paid`]) || 0) - moved[side], 0))
  return { sender: left('sender'), receiver: left('receiver') }
}

/** What each party paid beyond their part — the price lowered after the money was taken: owed back to them (tms.refund) */
export function waybillOverpaid(bill: WaybillValues): Dues {
  const dues = waybillDues(bill)
  const over = (side: PaySide) => round2(Math.max((Number(bill[`${side}Paid`]) || 0) - dues[side], 0))
  return { sender: over('sender'), receiver: over('receiver') }
}

/**
 * Paid when nothing is owed (`moved` when the receiver's part went onto a return bill); `partial` when part was paid
 * and the rest is still owed at a counter; else unpaid
 */
export function waybillPaymentOf(bill: WaybillValues): WaybillPaymentStatus {
  const over = waybillOverpaid(bill)
  if (paySides.some((side) => over[side] > 0)) return 'overpaid'
  const owed = waybillOwed(bill)
  const owing = paySides.filter((side) => owed[side] > 0)
  const paid = (Number(bill.senderPaid) || 0) + (Number(bill.receiverPaid) || 0)
  if (!owing.length) return (Number(bill.receiverMoved) || 0) > 0 ? 'moved' : paid > 0 ? 'paid' : 'unpaid'
  return paid > 0 ? 'partial' : 'unpaid'
}
